import React, { useState, useCallback } from 'react';
import { logger } from '../utils/logger';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface ToastContextType {
  toasts: Toast[];
  showToast: (toast: Omit<Toast, 'id'>) => void;
  hideToast: (id: string) => void;
  clearAllToasts: () => void;
}

// Toast utility functions
export const showSuccessToast = (title: string, message: string, duration = 4000) => {
  const event = new CustomEvent('show-toast', {
    detail: { type: 'success', title, message, duration }
  });
  window.dispatchEvent(event);
};

export const showErrorToast = (title: string, message: string, duration = 6000, action?: { label: string; onClick: () => void }) => {
  const event = new CustomEvent('show-toast', {
    detail: { type: 'error', title, message, duration, action }
  });
  window.dispatchEvent(event);
};

export const showWarningToast = (title: string, message: string, duration = 5000) => {
  const event = new CustomEvent('show-toast', {
    detail: { type: 'warning', title, message, duration }
  });
  window.dispatchEvent(event);
};

export const showInfoToast = (title: string, message: string, duration = 4000) => {
  const event = new CustomEvent('show-toast', {
    detail: { type: 'info', title, message, duration }
  });
  window.dispatchEvent(event);
};

/**
 * Hook for managing toast notifications
 */
export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration || 4000
    };

    setToasts(prev => [...prev, newToast]);

    // Auto-hide toast after duration
    const duration = newToast.duration ?? 4000;
    if (duration > 0) {
      setTimeout(() => {
        hideToast(id);
      }, duration);
    }

    // Log toast for debugging
    logger.info(`Toast shown: ${toast.type} - ${toast.title}`, { message: toast.message }, 'Toast');
  }, []);

  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
    logger.debug('All toasts cleared', undefined, 'Toast');
  }, []);

  return {
    toasts,
    showToast,
    hideToast,
    clearAllToasts
  };
}

/**
 * Hook for listening to global toast events
 */
export function useGlobalToastEvents() {
  const { showToast } = useToast();

  const handleToastEvent = useCallback((event: CustomEvent) => {
    const { type, title, message, duration, action } = event.detail;
    showToast({ type, title, message, duration, action });
  }, [showToast]);

  // Set up global event listener
  React.useEffect(() => {
    window.addEventListener('show-toast', handleToastEvent as EventListener);
    return () => {
      window.removeEventListener('show-toast', handleToastEvent as EventListener);
    };
  }, [handleToastEvent]);

  return { showToast };
}
